import { ApplicationConfig, ProjectInfo, SystemTypes } from '@frontend-site/shared/data';
import { MenuItem } from '../../../../models';
import { MENU_IDS } from '../../../menu-ids';
import { CorrectionsMenuFactory } from '../corrections-menu-factory';

export function createOverviewSubMenu(
    projectInfo: ProjectInfo,
    appConfig: ApplicationConfig,
    legacyAppVersion: string | undefined,
    isForesightAllComponentsModuleNewUi: boolean,
    isUnifiedProjectPlanNewUi = false
): MenuItem[] {
    const items: MenuItem[] = [];

    if (showOrgOverviewMenu(projectInfo, appConfig)) {
        items.push({
            id: MENU_IDS.PROJECT.OVERVIEW.ORG_OVERVIEW,
            label: 'Enumerations.ApplicationModules.OVERVIEW_MENU.ORG_OVERVIEW',
            routePath: `/tm/${projectInfo.id}/project/organizationOverview`
        });
    }

    if (showCustomDashboardMenu(projectInfo)) {
        items.push({
            id: MENU_IDS.PROJECT.OVERVIEW.CUSTOM_DASHBOARD,
            label: 'Enumerations.ApplicationModules.OVERVIEW_MENU.CUSTOM_DASHBOARD',
            routePath: `/tm/${projectInfo.id}/project/customDashboard`
        });
    }
    if (showUnifiedProjectPlanMenu(projectInfo, isUnifiedProjectPlanNewUi)) {
        items.push({
            id: MENU_IDS.PROJECT.OVERVIEW.PROJECT_PLAN,
            label: 'Enumerations.ApplicationModules.OVERVIEW_MENU.PROJECT_PLAN',
            routePath: `${projectInfo.id}/projectPlan`
        });
    }
    if (!projectInfo.isIcr3Project() && showProjectDashboardMenu(projectInfo)) {
        items.push({
            id: MENU_IDS.PROJECT.OVERVIEW.PROJECT_DASHBOARD,
            label: `Enumerations.ApplicationModules.OVERVIEW_MENU.${
                projectInfo.isSalesforceProject() ? 'salesforce.PROJECT_DASHBOARD' : 'PROJECT_DASHBOARD'
            }`,
            routePath: `/tm/${projectInfo.id}/project/dashboard`
        });
    }
    if (projectInfo.isIcr3Project() && projectInfo.hasActiveEtl) {
        items.push({
            id: MENU_IDS.PROJECT.OVERVIEW.PROJECT_PLAN,
            label: 'Enumerations.ApplicationModules.OVERVIEW_MENU.PROJECT_PLAN',
            routePath: `${projectInfo.id}/projectPlan`
        });
    }

    if (showCorrectionPlanMenu(projectInfo)) {
        items.push(CorrectionsMenuFactory.createCorrectionPlanMenuItem(projectInfo));
    }

    if (showAllComponentsMenu(projectInfo)) {
        items.push({
            id: MENU_IDS.PROJECT.OVERVIEW.SF_COMPONENTS_LIST,
            label: `Enumerations.ApplicationModules.OVERVIEW_MENU.ALL_COMPONENTS`,
            routePath: isForesightAllComponentsModuleNewUi
                ? `foresight/overview/${projectInfo.id}/all-components`
                : `/tm/${projectInfo.id}/project/sfComponents`,
            settings: { showDivider: showDivider(projectInfo, appConfig) }
        });
    }

    if (showAutomationComponentsMenu(projectInfo)) {
        items.push({
            id: MENU_IDS.PROJECT.OVERVIEW.AUTOMATION_COMPONENTS,
            label: `Enumerations.ApplicationModules.OVERVIEW_MENU.AUTOMATION_COMPONENTS`,
            routePath: `/foresight/overview/${projectInfo.id}/automation-overview`
        });
    }

    if (showEmailAlertsMenu(projectInfo)) {
        items.push({
            id: MENU_IDS.PROJECT.OVERVIEW.SF_WORKFLOW_EMAIL_ALERT,
            label: `Enumerations.ApplicationModules.OVERVIEW_MENU.EMAIL_ALERT`,
            routePath: `/foresight/overview/${projectInfo.id}/email-alerts`
        });
    }

    if (showPotentialTechnicalDebtMenu(projectInfo)) {
        items.push({
            id: MENU_IDS.PROJECT.OVERVIEW.POTENTIAL_TECHNICAL_DEBT,
            label: `Enumerations.ApplicationModules.OVERVIEW_MENU.POTENTIAL_TECHNICAL_DEBT`,
            routePath: `/tm/${projectInfo.id}/project/organizationOverview/potentialTechnicalDebt`,
            settings: {
                showDivider: !showAllComponentsMenu(projectInfo) && showDivider(projectInfo, appConfig)
            }
        });
    }

    if (showDeliveredSpAndSapNotesMenu(projectInfo)) {
        items.push({
            id: MENU_IDS.PROJECT.OVERVIEW.SUPPORT_PACKAGES,
            label: 'Enumerations.ApplicationModules.OVERVIEW_MENU.SUPPORT_PACKAGES',
            routePath: `/tm/${projectInfo.id}/project/content/deliveredSp`
        });
        items.push({
            id: MENU_IDS.PROJECT.OVERVIEW.SAP_NOTES,
            label: 'Enumerations.ApplicationModules.OVERVIEW_MENU.SAP_NOTES',
            routePath: `/tm/${projectInfo.id}/project/content/sapNotesInsights`
        });
    }

    if (showChangedContentMenu(projectInfo, appConfig)) {
        items.push({
            id: MENU_IDS.PROJECT.OVERVIEW.CHANGED_CONTENT,
            label: 'Enumerations.ApplicationModules.OVERVIEW_MENU.CHANGED_CONTENT',
            routePath: `/tm/${projectInfo.id}/project/content`
        });
    }
    if (!projectInfo.isIcr3Project() && showSatelliteMenu(projectInfo)) {
        items.push({
            id: MENU_IDS.PROJECT.OVERVIEW.SATELLITE_OVERVIEW,
            label: 'Enumerations.ApplicationModules.OVERVIEW_MENU.SATELLITE_OVERVIEW.header',
            routePath: `${location.protocol}//${location.hostname}/app/${legacyAppVersion}/build/satelliteMain.index.html?projectId=${projectInfo.projectId}`,
            settings: { openInNewTab: true }
        });
    }

    if (!projectInfo.isIcr3Project() && !projectInfo.isS4GreenProject() && showProjectDetailsMenu(projectInfo)) {
        items.push({
            id: MENU_IDS.PROJECT.OVERVIEW.PROJECT_DETAILS,
            label: 'Enumerations.ApplicationModules.OVERVIEW_MENU.PROJECT_DETAILS',
            routePath: `${projectInfo.id}/overview/project-details`,
            settings: { showDivider: true }
        });
    }
    return items;
}

export function showOrgOverviewMenu(projectInfo: ProjectInfo, appConfig: ApplicationConfig): boolean {
    return projectInfo.modular && projectInfo.hasModule('ORG_OVERVIEW') && (projectInfo.orgOverviewVisible || appConfig.isExecuteUser);
}
export function showCustomDashboardMenu(projectInfo: ProjectInfo): boolean {
    return !projectInfo.modular || projectInfo.hasTestsModule() || projectInfo.rdxEnabled;
}

// RDX-03777: S/4Convert-new Project Plan — shown for S4Convert-new projects with an ETL, gated by the
// UnifiedProjectPlan new-UI flag (GA from 26.12).
function showUnifiedProjectPlanMenu(projectInfo: ProjectInfo, isUnifiedProjectPlanNewUi: boolean): boolean {
    return projectInfo.isS4ConvertNew() && projectInfo.hasActiveEtl && isUnifiedProjectPlanNewUi;
}

function showProjectDashboardMenu(projectInfo: ProjectInfo): boolean {
    return !projectInfo.modular || projectInfo.hasTestsModule();
}

function showCorrectionPlanMenu(projectInfo: ProjectInfo): boolean {
    return (
        projectInfo.isS4Project() ||
        projectInfo.isS4GreenProject() ||
        projectInfo.isCleanCoreProject() ||
        (projectInfo.isSpsNew() && !projectInfo.hrOnly)
    );
}

function showAllComponentsMenu(projectInfo: ProjectInfo): boolean {
    return (
        !projectInfo.isRestrictedApp() &&
        projectInfo.isSalesforceProject() &&
        (!projectInfo.modular || projectInfo.hasModule('COMPONENTS_ATTRIBUTES'))
    );
}

function showAutomationComponentsMenu(projectInfo: ProjectInfo): boolean {
    return projectInfo.hasActiveEtl && projectInfo.hasModule('AUTOMATION_COMPONENTS') && projectInfo.isSalesforceProject();
}

function showEmailAlertsMenu(projectInfo: ProjectInfo): boolean {
    return showAutomationComponentsMenu(projectInfo);
}

function showPotentialTechnicalDebtMenu(projectInfo: ProjectInfo): boolean {
    return !projectInfo.isRestrictedApp() && projectInfo.isSalesforceProject();
}

function showDeliveredSpAndSapNotesMenu(projectInfo: ProjectInfo): boolean {
    return projectInfo.isS4AccelerateProject() || projectInfo.isSupportPackageProject();
}

function showChangedContentMenu(projectInfo: ProjectInfo, appConfig: ApplicationConfig): boolean {
    return (
        !projectInfo.isOracleUpgradeProject() &&
        !projectInfo.isOracleOnlinePatchesProject() &&
        !projectInfo.isUpgradeProject() &&
        !projectInfo.isSupportPackageProject() &&
        !projectInfo.isS4ConversionProject() &&
        !projectInfo.isIcr3Project() &&
        !projectInfo.isS4AccelerateProject() &&
        !appConfig.isSelectedUatOnly(projectInfo.systemId) &&
        (projectInfo.isIAProject() || (projectInfo.isTestingOnlyProject() && isAllowFPR(projectInfo, appConfig))) &&
        !projectInfo.testCatalog &&
        (projectInfo.isSapIAProject() || projectInfo.isOraclePatchesProject()) &&
        (!projectInfo.modular || projectInfo.rdxEnabled)
    );
}

function showSatelliteMenu(projectInfo: ProjectInfo): boolean {
    return projectInfo.hasSatellite && (projectInfo.isSapIAProject() || projectInfo.isVfStandaloneProject());
}

function showProjectDetailsMenu(projectInfo: ProjectInfo): boolean {
    return projectInfo.rdxEnabled;
}

function isAllowFPR(projectInfo: ProjectInfo, appConfig: ApplicationConfig): boolean {
    return appConfig.allowedFPR || (projectInfo.panayaUser && projectInfo.systemType === SystemTypes.SAP);
}

function showDivider(projectInfo: ProjectInfo, appConfig: ApplicationConfig): boolean {
    return showOrgOverviewMenu(projectInfo, appConfig) || projectInfo.rdxEnabled || projectInfo.hasTestsModule();
}
